import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, 
  ZapOff, 
  Wifi, 
  WifiOff, 
  RefreshCw,
  Wallet,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Play,
  Square
} from 'lucide-react';
import { NativeButton } from '../../components/NativeComponents';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');

const residentChargingService = {
  getStations: async () => {
    const response = await fetch(`${API_URL}/api/resident/charging/stations`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  },
  startCharge: async (stationId, connectorId) => {
    const response = await fetch(`${API_URL}/api/resident/charging/start`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ station_id: stationId, connector_id: connectorId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Şarj başlatılamadı');
    return data;
  },
  stopCharge: async (stationId, connectorId) => {
    const response = await fetch(`${API_URL}/api/resident/charging/stop`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ station_id: stationId, connector_id: connectorId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Şarj durdurulamadı');
    return data;
  }
};

export default function ResidentChargingPage() {
  const { user, simulationActive } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (simulationActive) return; // Simülasyonda veri çekme
    if (showRefresh) setRefreshing(true);
    try {
      const result = await residentChargingService.getStations();
      setData(result);
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [simulationActive]);

  // Aktif şarj oturumu var mı kontrol et
  const hasActiveSession = data?.stations?.some(s => 
    s.connectors?.some(c => c.has_active_session)
  );

  useEffect(() => {
    if (simulationActive) {
      setLoading(false);
      return;
    }
    fetchData();
    // Aktif şarj varsa 5 saniyede bir, yoksa 30 saniyede bir güncelle
    const intervalMs = hasActiveSession ? 5000 : 30000;
    const interval = setInterval(() => fetchData(false), intervalMs);
    return () => clearInterval(interval);
  }, [fetchData, hasActiveSession]);

  const handleStartCharge = async (stationId, connectorId) => {
    setActionLoading(`start-${stationId}-${connectorId}`);
    try {
      await residentChargingService.startCharge(stationId, connectorId);
      toast.success('Şarj başlatıldı! Kablonuzu takın.');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopCharge = async (stationId, connectorId) => {
    setActionLoading(`stop-${stationId}-${connectorId}`);
    try {
      await residentChargingService.stopCharge(stationId, connectorId);
      toast.success('Şarj durduruldu!');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const getConnectorStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'bg-green-500';
      case 'charging': return 'bg-blue-500';
      case 'preparing': return 'bg-yellow-500';
      case 'finishing': return 'bg-purple-500';
      case 'faulted': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getConnectorStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'Müsait';
      case 'charging': return 'Şarj Ediyor';
      case 'preparing': return 'Hazırlanıyor';
      case 'finishing': return 'Tamamlanıyor';
      case 'faulted': return 'Arızalı';
      case 'unavailable': return 'Kullanılamaz';
      default: return status || 'Bilinmiyor';
    }
  };

  const formatDuration = (startTime) => {
    if (!startTime) return '-';
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${hours}s ${minutes}dk`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Simülasyon modunda uyarı göster
  if (simulationActive) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Simülasyon Modunda Kullanılamaz
          </h2>
          <p className="text-yellow-700 dark:text-yellow-300">
            Bu özellik simülasyon modunda kullanılamaz. Gerçek kullanıcı girişi gerektirir.
          </p>
        </div>
      </div>
    );
  }

  const balanceInfo = data?.balance_info || {};
  const isPrepaid = balanceInfo.payment_type === 'prepaid';

  return (
    <div className="space-y-6 p-4 max-w-2xl mx-auto" data-testid="resident-charging-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Şarj Kontrol</h1>
          <p className="text-muted-foreground text-sm">İstasyonu seçin ve şarjı başlatın</p>
        </div>
        <NativeButton 
          variant="outline" 
          size="sm"
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </NativeButton>
      </div>

      {/* Bakiye/Limit Kartı */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPrepaid ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
            <Wallet className={`w-6 h-6 ${isPrepaid ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
          <div className="flex-1">
            {isPrepaid ? (
              <>
                <p className="text-sm text-muted-foreground">Bakiyeniz</p>
                <p className="text-2xl font-bold text-foreground">
                  {balanceInfo.balance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Aylık Kullanım / Limit</p>
                <p className="text-2xl font-bold text-foreground">
                  {balanceInfo.monthly_usage?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  <span className="text-base text-muted-foreground font-normal">
                    {' / '}{balanceInfo.monthly_limit?.toLocaleString('tr-TR')} ₺
                  </span>
                </p>
              </>
            )}
          </div>
          {balanceInfo.card_id && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">RFID Kart</p>
              <p className="text-sm font-mono text-foreground">{balanceInfo.card_id}</p>
            </div>
          )}
        </div>
        {isPrepaid && balanceInfo.balance < 10 && (
          <div className="mt-3 flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Bakiyeniz düşük. Lütfen yükleyin.</span>
          </div>
        )}
      </div>

      {/* İstasyon Listesi */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">İstasyonlar</h2>
        
        {(!data?.stations || data.stations.length === 0) ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <ZapOff className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Kullanılabilir istasyon bulunamadı</p>
          </div>
        ) : (
          data.stations.map((station) => (
            <div key={station.id} className="bg-card border border-border rounded-xl overflow-hidden">
              {/* İstasyon Header */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">{station.name}</p>
                    <p className="text-xs text-muted-foreground">{station.ocpp_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {station.is_online ? (
                    <span className="flex items-center gap-1 text-green-600 text-sm">
                      <Wifi className="w-4 h-4" />
                      Çevrimiçi
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-500 text-sm">
                      <WifiOff className="w-4 h-4" />
                      Çevrimdışı
                    </span>
                  )}
                </div>
              </div>

              {/* Konnektörler */}
              <div className="divide-y divide-border">
                {station.connectors?.map((connector) => (
                  <div key={connector.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${getConnectorStatusColor(connector.status)}`}></div>
                        <div>
                          <p className="font-medium text-foreground">
                            Konnektör {connector.id + 1}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getConnectorStatusText(connector.status)}
                          </p>
                        </div>
                      </div>

                      {/* Şarj Durumu veya Butonlar */}
                      {connector.has_active_session ? (
                        <div className="flex items-center gap-4">
                          {/* Aktif Şarj Bilgisi */}
                          <div className="text-right">
                            <div className="flex items-center gap-2 text-blue-600">
                              <Zap className="w-4 h-4" />
                              <span className="font-bold">
                                {(connector.energy_consumed / 1000).toFixed(2)} kWh
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {formatDuration(connector.start_time)}
                            </div>
                            <p className="text-sm font-medium text-foreground">
                              {connector.total_cost?.toFixed(2)} ₺
                            </p>
                          </div>
                          
                          {/* Durdur Butonu */}
                          <NativeButton
                            variant="destructive"
                            size="sm"
                            onClick={() => handleStopCharge(station.id, connector.id)}
                            disabled={actionLoading === `stop-${station.id}-${connector.id}` || !station.is_online}
                          >
                            {actionLoading === `stop-${station.id}-${connector.id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Square className="w-4 h-4 mr-1" />
                                Durdur
                              </>
                            )}
                          </NativeButton>
                        </div>
                      ) : (
                        <NativeButton
                          variant="default"
                          size="sm"
                          onClick={() => handleStartCharge(station.id, connector.id)}
                          disabled={
                            actionLoading === `start-${station.id}-${connector.id}` || 
                            !station.is_online ||
                            connector.status?.toLowerCase() === 'faulted' ||
                            connector.status?.toLowerCase() === 'unavailable'
                          }
                        >
                          {actionLoading === `start-${station.id}-${connector.id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              Başlat
                            </>
                          )}
                        </NativeButton>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bilgi Notu */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100">Nasıl Kullanılır?</p>
            <ol className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1 list-decimal list-inside">
              <li>Kablonuzu araca ve istasyona takın</li>
              <li>"Başlat" butonuna basın</li>
              <li>Şarj tamamlandığında "Durdur" butonuna basın</li>
              <li>Kablonuzu çıkarın</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
